import { Pagination } from '../../../../models/pagination.model';
import { Resource } from '../../resource/models';

export interface ExternalPhysicalClass {
  _id?: string;
  title: string;
  subject: string;
  medium: 'SINHALA' | 'ENGLISH' | 'TAMIL';
  grade: string;
  classType: 'Individual' | 'Group' | 'Institute';
  location: {
    district: string;
    city: string;
    venue?: string;
  };
  schedule?: string;
  feeDetails?: string;
}

export interface VerificationDocuments {
  nicFront?: Resource | string;
  nicBack?: Resource | string;
  academicCertificate?: Resource | string;
}

export interface TutorInfo {
  subjects?: string[];
  grades?: string[];
  mediums?: string[];
  classFormats?: string[];
  districts?: string[];
  cities?: string[];
  ratePerHour?: number;
  ratePerMonth?: number;
  pricingDetails?: string;
  whatsAppNumber?: string;
  youtubeChannel?: string;
  socialLinks?: {
    facebook?: string;
    linkedin?: string;
    website?: string;
  };
  bannerImage?: Resource | string;
  isVerifiedTutor?: boolean;
  verificationDocuments?: VerificationDocuments;
  externalClasses?: ExternalPhysicalClass[];
  isPublic?: boolean;
}

export interface UserProfileRequestDTO {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  biography?: string;
  avatar?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  tutorInfo?: TutorInfo;
}

export enum UserStatus {
  UNVERIFIED = 'UNVERIFIED',
  VERIFIED = 'VERIFIED',
  BLOCKED = 'BLOCKED',
  PENDING_PARENTAL_CONSENT = 'PENDING_PARENTAL_CONSENT',
}

export enum UserRoles {
  ADMIN = 'ADMINISTRATOR',
  STUDENT = 'STUDENT',
  INSTRUCTOR = 'INSTRUCTOR',
}

enum UserGender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export interface UserProfile {
  personal: {
    firstName: string;
    lastName: string;
    gender?: UserGender;
    email?: string;
    mobile?: string;
    biography?: string;
    dateOfBirth?: Date;
    parentEmail?: string;
    avatar?: Resource;
  };
  professional?: {
    jobTitle?: string;
    experience?: string;
    certifications?: string[];
    educationalQualifications?: string[];
  };
  address?: {
    country?: string;
    province?: string;
    district?: string;
    city?: string;
    addressLine1?: string;
    addressLine2?: string;
    postalCode?: string;
  };
  tutorInfo?: TutorInfo;
}

export interface User {
  id?: string;
  _id?: string;
  studentId?: string;
  username: string;
  slug?: string;
  role?: UserRoles;
  isSuperAdmin?: boolean;
  isManuallyAdded?: boolean;
  addedBy?: any;
  name: {
    firstName: string;
    lastName: string;
  };
  email?: string;
  mobile?: string;
  password: string;
  profile: UserProfile;
  pendingTutorProfileUpdates?: any;
}

// TODO: Should merge this with User interface
// Created temporarily to accomodate _id and status fields
export interface UserCompatible extends User {
  status: UserStatus;
  _id: string;
  linkedAccounts?: {
    zoom: {
      userId: string;
      username: string;
      accountType?: 'internal' | 'public';
    };
  };
}

export interface AddManualStudentPayload {
  firstName: string;
  lastName?: string;
  email?: string;
  mobile?: string;
  studentId?: string;
  liveClassId?: string;
  courseId?: string;
  notes?: string;
}

export interface UserRequestDTO {
  _id?: string;
  username: string;
  role?: UserRoles;
  name: {
    firstName: string;
    lastName: string;
  };
  email?: string;
  mobile?: string;
  password: string;
  passwordSalt?: string;
}

export interface linkedAccountsResponse {
  provider: string;
  userId: string;
  username: string;
  accountType?: 'internal' | 'public';
}

export interface UserResponseDTO {
  username: string;
  slug?: string;
  role: UserRoles;
  status: UserStatus;
  name: {
    firstName: string;
    lastName: string;
  };
  email?: string;
  mobile?: string;
  profile?: UserProfile;
  linkedAccounts?: linkedAccountsResponse[];
}

export interface UsersResponse extends Pagination {
  docs: User[];
}

export type UserExceptPassword = Omit<User, 'password'>;
